import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RootState, AppDispatch } from '../store';
import { setCredentials, clearCredentials, setLoading } from '../store/slices/authSlice';
import { switchWorkspace as bootSwitchWorkspace } from '../store/slices/bootSlice';
import { getAccessToken, setAccessToken, logout as centralLogout } from '../lib/centralAuth';

/**
 * Auth hook that integrates with Central authentication.
 *
 * v2.3.0 COMPLIANT:
 * - Token-only management. No JWT decode. No user extraction.
 * - User identity comes exclusively from switch-context (bootSlice).
 * - JWT is for API auth only (Bearer token), never for UI authority.
 *
 * TOKEN STORAGE: In-memory only (via centralAuth module)
 * - Page reload clears token (requires re-authentication)
 * - This is secure by design - XSS cannot steal tokens from memory
 */
export function useAuth() {
  const dispatch = useDispatch<AppDispatch>();
  const { token, isAuthenticated, isLoading } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    const storedToken = getAccessToken();

    if (storedToken) {
      // No decode — just register token presence
      dispatch(setCredentials({ token: storedToken }));
    } else {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const queryClient = useQueryClient();

  const login = (token: string) => {
    setAccessToken(token);
    dispatch(setCredentials({ token }));
  };

  const logout = () => {
    centralLogout();
    dispatch(clearCredentials());
  };

  /**
   * Client-side workspace switch.
   * Swaps currentWorkspace in Redux and resets React Query cache.
   * Same logic as WorkspaceSwitcher component, exposed for shell adapter use.
   */
  const switchWorkspace = useCallback(
    async (workspaceId: string): Promise<void> => {
      dispatch(bootSwitchWorkspace(workspaceId));
      queryClient.resetQueries();
    },
    [dispatch, queryClient]
  );

  return {
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    /** Auth source — always 'central' when authenticated, null otherwise */
    authSource: isAuthenticated ? ('central' as const) : null,
    /** Switch workspace (client-side swap + cache reset) */
    switchWorkspace,
  };
}
